/**
 * Génération des icônes de marqueurs de la carte.
 *
 * Portage des méthodes `create*Icon` de `MapManager.kt` et
 * `RadarLayerManager.kt`, qui dessinent chaque icône au Canvas plutôt que
 * d'embarquer des images : la couleur de la trace fait partie du dessin, et
 * une palette d'images figées ne pourrait pas suivre.
 *
 * Toutes les proportions sont exprimées en fraction de la taille de l'icône,
 * exactement comme côté Android, pour que le rendu soit identique quelle que
 * soit la densité d'écran.
 */

import { canvasPixelRatio } from './canvasScale'

/** Côté des icônes de trace, en points de dessin. */
export const ICON_BASE_SIZE = 40

/** Les marqueurs personnalisés sont plus grands, pour rester lisibles. */
export const CUSTOM_MARKER_SIZE = 50

function createCanvas(size: number): {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  scale: number
} {
  const canvas = document.createElement('canvas')
  // Densité reprise telle quelle, comme partout ailleurs. Le plafond à 2 qui
  // vivait ici livrait l'image à MapLibre avec `pixelRatio: 2` ; sur un écran
  // en densité 3 le moteur la réétirait, alors que le fond de carte, lui,
  // dessine à la densité réelle — les symboles sortaient plus mous que la
  // carte qui les porte, l'inverse de ce que le plafond cherchait.
  const scale = canvasPixelRatio()
  canvas.width = Math.round(size * scale)
  canvas.height = Math.round(size * scale)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Contexte 2D indisponible : impossible de dessiner les marqueurs.')
  }

  ctx.scale(scale, scale)
  return { canvas, ctx, scale }
}

export interface MarkerImage {
  readonly data: ImageData
  readonly pixelRatio: number
}

function toImage(canvas: HTMLCanvasElement, scale: number): MarkerImage {
  const ctx = canvas.getContext('2d')!
  return {
    data: ctx.getImageData(0, 0, canvas.width, canvas.height),
    pixelRatio: scale,
  }
}

/** Disque noir et anneau à la couleur de la trace, socle commun aux pastilles. */
function drawDisc(
  ctx: CanvasRenderingContext2D,
  size: number,
  ringColor: string,
  ringWidth: number,
): void {
  const center = size / 2

  ctx.fillStyle = '#000000'
  ctx.beginPath()
  ctx.arc(center, center, center, 0, Math.PI * 2)
  ctx.fill()

  ctx.strokeStyle = ringColor
  ctx.lineWidth = ringWidth
  ctx.beginPath()
  ctx.arc(center, center, center - ringWidth / 2, 0, Math.PI * 2)
  ctx.stroke()
}

/** Damier d'arrivée, sur une grille de quatre cases de côté. */
function drawChecker(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  width: number,
  height: number,
  columns: number,
): void {
  const cellWidth = width / columns
  const cellHeight = height / 4

  ctx.fillStyle = '#FFFFFF'
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < columns; col++) {
      if ((row + col) % 2 !== 0) continue
      ctx.fillRect(
        left + col * cellWidth,
        top + row * cellHeight,
        cellWidth,
        cellHeight,
      )
    }
  }
}

/** Départ : triangle de lecture blanc sur pastille cerclée. */
export function createStartIcon(
  trackColor: string,
  size = ICON_BASE_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  drawDisc(ctx, size, trackColor, size / 12)

  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.moveTo(0.38 * size, 0.25 * size)
  ctx.lineTo(0.38 * size, 0.75 * size)
  ctx.lineTo(0.75 * size, 0.5 * size)
  ctx.closePath()
  ctx.fill()

  return toImage(canvas, scale)
}

/**
 * Départ fictif : le triangle de départ barré de rouge.
 *
 * Utilisé dès qu'un départ réel est configuré — le départ du fichier n'est
 * alors plus le point de départ effectif.
 */
export function createFictiveStartIcon(
  trackColor: string,
  size = ICON_BASE_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  drawDisc(ctx, size, trackColor, size / 12)

  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.moveTo(0.38 * size, 0.25 * size)
  ctx.lineTo(0.38 * size, 0.75 * size)
  ctx.lineTo(0.75 * size, 0.5 * size)
  ctx.closePath()
  ctx.fill()

  ctx.strokeStyle = '#FF0000'
  ctx.lineWidth = size / 10
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(0.2 * size, 0.8 * size)
  ctx.lineTo(0.8 * size, 0.2 * size)
  ctx.stroke()

  return toImage(canvas, scale)
}

/** Arrivée : damier blanc découpé en cercle. */
export function createFinishIcon(
  trackColor: string,
  size = ICON_BASE_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  drawDisc(ctx, size, trackColor, size / 12)

  const center = size / 2
  ctx.save()
  // Le damier est découpé par un cercle plus petit que la pastille, sinon
  // les cases d'angle mordraient sur l'anneau.
  ctx.beginPath()
  ctx.arc(center, center, 0.7 * center, 0, Math.PI * 2)
  ctx.clip()

  const inset = 0.25 * size
  drawChecker(ctx, inset, inset, size - 2 * inset, size - 2 * inset, 4)
  ctx.restore()

  return toImage(canvas, scale)
}

/**
 * Départ et arrivée confondus, pour les boucles.
 *
 * Moitié gauche en triangle de départ, moitié droite en damier : une boucle
 * n'a qu'un seul point pour les deux, et deux pastilles superposées seraient
 * illisibles.
 */
export function createStartFinishIcon(
  trackColor: string,
  size = ICON_BASE_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  drawDisc(ctx, size, trackColor, size / 12)

  const center = size / 2

  ctx.save()
  ctx.beginPath()
  ctx.arc(center, center, 0.78 * center, 0, Math.PI * 2)
  ctx.clip()

  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.moveTo(0.22 * size, 0.28 * size)
  ctx.lineTo(0.22 * size, 0.72 * size)
  ctx.lineTo(0.55 * size, 0.5 * size)
  ctx.closePath()
  ctx.fill()

  const inset = 0.2 * size
  drawChecker(ctx, center, inset, size - center - inset, size - 2 * inset, 2)
  ctx.restore()

  return toImage(canvas, scale)
}

/** Pastille kilométrique : numéro blanc sur disque cerclé. */
export function createKmIcon(
  trackColor: string,
  label: string,
  size = ICON_BASE_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  drawDisc(ctx, size, trackColor, size / 10)

  const center = size / 2
  ctx.fillStyle = '#FFFFFF'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `bold ${fittedFontSize(ctx, label, size)}px ${MARKER_FONT}`
  ctx.fillText(label, center, center)

  return toImage(canvas, scale)
}

/**
 * Taille de police tenant dans le disque.
 *
 * L'anneau occupe un dixième du rayon de chaque côté : le noir utile ne fait
 * que quatre cinquièmes du côté. Un nombre à trois chiffres — ou un « 12,5 »
 * — déborde de la police nominale et se retrouve rogné. L'application Android
 * a le même défaut ; on l'évite ici en resserrant la police juste assez.
 */
function fittedFontSize(
  ctx: CanvasRenderingContext2D,
  label: string,
  size: number,
): number {
  const nominal = 0.45 * size
  const available = 0.78 * size

  ctx.font = `bold ${nominal}px ${MARKER_FONT}`
  const width = ctx.measureText(label).width

  return width <= available ? nominal : nominal * (available / width)
}

/**
 * Marqueur personnalisé : losange bordé à la couleur de la trace.
 *
 * La forme losange distingue au premier coup d'œil les points d'intérêt de
 * la course des simples pastilles kilométriques.
 */
export function createDiamondIcon(
  trackColor: string,
  symbol: string,
  darkMode: boolean,
  size = CUSTOM_MARKER_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  const center = size / 2

  ctx.beginPath()
  ctx.moveTo(center, 0.05 * size)
  ctx.lineTo(0.95 * size, center)
  ctx.lineTo(center, 0.95 * size)
  ctx.lineTo(0.05 * size, center)
  ctx.closePath()

  ctx.fillStyle = darkMode ? '#000000' : '#FFFFFF'
  ctx.fill()

  ctx.strokeStyle = trackColor
  ctx.lineWidth = size / 10
  ctx.lineJoin = 'round'
  ctx.stroke()

  ctx.fillStyle = darkMode ? '#FFFFFF' : '#000000'
  ctx.font = `bold ${0.38 * size}px ${MARKER_FONT}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(symbol, center, center)

  return toImage(canvas, scale)
}

/** Radar : triangle d'avertissement orange à point d'exclamation. */
export function createRadarIcon(size = ICON_BASE_SIZE): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  ctx.beginPath()
  ctx.moveTo(0.5 * size, 0.05 * size)
  ctx.lineTo(0.95 * size, 0.9 * size)
  ctx.lineTo(0.05 * size, 0.9 * size)
  ctx.closePath()

  ctx.fillStyle = '#FF9800'
  ctx.fill()
  ctx.strokeStyle = '#E65100'
  ctx.lineWidth = 0.04 * size
  ctx.lineJoin = 'round'
  ctx.stroke()

  // Hampe légèrement trapézoïdale, comme sur un panneau routier.
  ctx.fillStyle = '#000000'
  ctx.beginPath()
  ctx.moveTo(0.5 * size - 0.08 * size, 0.32 * size)
  ctx.lineTo(0.5 * size + 0.08 * size, 0.32 * size)
  ctx.lineTo(0.5 * size + 0.056 * size, 0.62 * size)
  ctx.lineTo(0.5 * size - 0.056 * size, 0.62 * size)
  ctx.closePath()
  ctx.fill()

  ctx.beginPath()
  ctx.arc(0.5 * size, 0.73 * size, 0.088 * size, 0, Math.PI * 2)
  ctx.fill()

  return toImage(canvas, scale)
}

/** Point d'intérêt du fichier GPX : simple pastille cerclée de blanc. */
export function createWaypointIcon(
  trackColor: string,
  size = ICON_BASE_SIZE,
): MarkerImage {
  const { canvas, ctx, scale } = createCanvas(size)

  const center = size / 2
  const radius = size * 0.3

  ctx.fillStyle = trackColor
  ctx.beginPath()
  ctx.arc(center, center, radius, 0, Math.PI * 2)
  ctx.fill()

  ctx.strokeStyle = '#FFFFFF'
  ctx.lineWidth = size * 0.1
  ctx.stroke()

  return toImage(canvas, scale)
}

/**
 * Police des symboles de marqueurs.
 *
 * Les symboles peuvent être des emoji ou des flèches (↰ ↱) : la pile inclut
 * explicitement des polices couvrant ces plages, sans quoi certains systèmes
 * afficheraient un rectangle vide.
 */
/**
 * Police des étiquettes de marqueurs.
 *
 * La police d'interface vient **en premier**, les polices de symboles ne
 * servent que de secours. Le repli s'opérant glyphe par glyphe, les flèches de
 * dérivation restent dessinées par une police de symboles, mais les chiffres
 * et les lettres reviennent à la police d'interface.
 *
 * L'ordre inverse, qui semblait plus sûr pour les symboles, faisait dessiner
 * les chiffres par une police d'émojis : deux fois trop larges — les pastilles
 * paraissaient étirées — et surtout **gris foncé**, une police à glyphes
 * colorés ignorant la couleur de remplissage demandée.
 */
export const MARKER_FONT =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, ' +
  'sans-serif, "Segoe UI Symbol", "Noto Sans Symbols 2", ' +
  '"Apple Color Emoji", "Noto Color Emoji"'

/**
 * Clé d'identité d'une image de marqueur.
 *
 * La couleur et le symbole étant cuits dans le dessin, chaque combinaison est
 * une image distincte. La clé sert à ne la générer qu'une fois.
 */
export function iconKey(
  base: string,
  color: string,
  variant?: string,
): string {
  const normalized = color.replace('#', '').toUpperCase()
  return variant
    ? `icon-${base}-${normalized}-${variant}`
    : `icon-${base}-${normalized}`
}
